import { request } from '@/api/apiUtils';
import { createEntityQuery } from '@/data/createEntityQuery';
import type { EntityDefinition, FetcherEntry } from '@/data/types';
import type { IRelations } from '@/types';

/** Root: query-key prefix for this entity's slice of the cache (ADR 0010).
 *
 *  A root of its own rather than a slice of `characters`: a relation write
 *  changes no character read anybody renders, so it should not refetch them. */
export const RELATIONSHIPS_ROOT = ['relationships'] as const;

interface RelationshipsAllParams {
    projectId: number;
    version?: string;
}

interface RelationsIndexPage {
    data: IRelations[];
    meta?: { current_page?: number; last_page?: number };
}

/** Relations per request. `per_page` is validated (≥1) but uncapped; this is
 *  one request for any realistic cast. */
const PER_PAGE = 1000;

/** Backstop against a paginator that never advances `current_page`. */
const MAX_PAGES = 100;

const fetchers = {
    /**
     * Every relation in the project, in one list. The map needs both
     * directions — a relation owned by B about A belongs on A's map too — and
     * the index only filters by owner, so reading per character would miss the
     * incoming half.
     */
    all: {
        key: ({ projectId, version }: RelationshipsAllParams) => [...RELATIONSHIPS_ROOT, 'all', { projectId, version }] as const,
        fetch: async ({ projectId, version }: RelationshipsAllParams): Promise<IRelations[]> => {
            const relations: IRelations[] = [];
            for (let page = 1; page <= MAX_PAGES; page++) {
                const params = new URLSearchParams({ per_page: String(PER_PAGE), page: String(page) });
                if (version) params.set('version', version);
                const response: RelationsIndexPage = await request(`projects/${projectId}/character-relations?${params}`);
                relations.push(...response.data);

                const { current_page: current = page, last_page: last = page } = response.meta ?? {};
                if (current >= last) break;
            }
            return relations;
        },
    },
    // biome-ignore lint/suspicious/noExplicitAny: required for generic type inference
} satisfies Record<string, FetcherEntry<any, any>>;

export const relationshipsEntity: EntityDefinition<typeof fetchers> = {
    name: 'relationships',
    roots: [RELATIONSHIPS_ROOT],
    fetchers,
};

export const useRelationshipsAll = createEntityQuery(relationshipsEntity, 'all');

export type { RelationshipsAllParams };
